export const INDUSTRIES = [
  "Food & Beverage",
  "Retail",
  "Hospitality",
  "Healthcare",
  "Construction",
  "Manufacturing",
  "Technology",
  "Professional Services",
  "Education",
  "Other",
];

export const CATEGORIES_BY_INDUSTRY: Record<string, string[]> = {
  "Food & Beverage": [
    "Produce",
    "Meat & Poultry",
    "Seafood",
    "Dairy",
    "Bakery",
    "Pantry",
    "Dry Goods",
    "Beverages",
    "Alcohol",
    "Packaging",
    "Cleaning & Chemicals",
    "Equipment",
    "Services",
    "Other",
  ],
  "Retail": [
    "Apparel & Accessories",
    "Electronics",
    "Home & Garden",
    "Toys & Games",
    "Beauty & Personal Care",
    "Sports & Outdoors",
    "Books & Stationery",
    "Packaging & Supplies",
    "Cleaning & Maintenance",
    "Fixtures & Displays",
    "Technology & Software",
    "Services",
    "Other",
  ],
  "Hospitality": [
    "Bedding & Linen",
    "Toiletries & Amenities",
    "Food & Beverage Supplies",
    "Cleaning & Chemicals",
    "Furniture & Fixtures",
    "Technology & Systems",
    "Guest Services",
    "Maintenance & Repair",
    "Uniforms & Apparel",
    "Marketing & Printing",
    "Utilities",
    "Services",
    "Other",
  ],
  "Healthcare": [
    "Medical Supplies",
    "Pharmaceuticals",
    "Diagnostic Equipment",
    "Personal Protective Equipment",
    "Cleaning & Sterilisation",
    "Office & Admin Supplies",
    "Technology & Systems",
    "Laboratory Supplies",
    "Patient Care Items",
    "Services",
    "Other",
  ],
  "Construction": [
    "Structural Materials",
    "Electrical",
    "Plumbing",
    "Concrete & Masonry",
    "Timber & Framing",
    "Roofing & Cladding",
    "Insulation",
    "Tools & Equipment",
    "Safety Equipment",
    "Hire & Plant",
    "Subcontractors",
    "Services",
    "Other",
  ],
  "Manufacturing": [
    "Raw Materials",
    "Components & Parts",
    "Packaging",
    "Machinery & Equipment",
    "Tooling",
    "Maintenance & Repair",
    "Safety Equipment",
    "Energy & Utilities",
    "Chemicals & Consumables",
    "Logistics & Freight",
    "Services",
    "Other",
  ],
  "Technology": [
    "Hardware",
    "Software & Licenses",
    "Cloud & Hosting",
    "Network & Communications",
    "Office Equipment",
    "Security",
    "Consulting & Professional Services",
    "Maintenance & Support",
    "Marketing & Advertising",
    "Training",
    "Other",
  ],
  "Professional Services": [
    "Office Supplies",
    "Software & Subscriptions",
    "Professional Development",
    "Marketing & Advertising",
    "Legal & Compliance",
    "Accounting & Finance",
    "Travel & Accommodation",
    "Technology & Equipment",
    "Facilities",
    "Services",
    "Other",
  ],
  "Education": [
    "Textbooks & Learning Materials",
    "Stationery & Art Supplies",
    "Technology & Equipment",
    "Laboratory Supplies",
    "Sports & Recreation",
    "Furniture & Fixtures",
    "Cleaning & Maintenance",
    "Catering & Canteen",
    "Services",
    "Other",
  ],
  "Other": [
    "Supplies",
    "Equipment",
    "Services",
    "Packaging",
    "Technology",
    "Marketing",
    "Facilities",
    "Other",
  ],
};

export function getCategoriesForIndustry(industry: string | null | undefined): string[] {
  if (!industry) return CATEGORIES_BY_INDUSTRY["Food & Beverage"];
  return CATEGORIES_BY_INDUSTRY[industry] ?? CATEGORIES_BY_INDUSTRY["Other"];
}

export const ALL_CATEGORIES = Array.from(
  new Set(Object.values(CATEGORIES_BY_INDUSTRY).flat()),
).sort();

const BILL_LENS_CATEGORY_PALETTE = {
  burgundy: "#9a3b4f",
  olive: "#607840",
  ochre: "#cf8a1a",
  kraft: "#b07d4a",
  terracotta: "#c25a3c",
  forest: "#2f7d57",
  ink2: "#607840",
  muted: "#8a8074",
  faint: "#aaa093",
} as const;

export const CATEGORY_COLORS: Record<string, string> = {
  // ── Food & Beverage ──────────────────────────────────────────────
  "Produce":                        BILL_LENS_CATEGORY_PALETTE.olive,
  "Meat & Poultry":                 BILL_LENS_CATEGORY_PALETTE.burgundy,
  "Seafood":                        BILL_LENS_CATEGORY_PALETTE.forest,
  "Dairy":                          BILL_LENS_CATEGORY_PALETTE.ochre,
  "Bakery":                         BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Pantry":                         BILL_LENS_CATEGORY_PALETTE.kraft,
  "Dry Goods":                      BILL_LENS_CATEGORY_PALETTE.kraft,
  "Beverages":                      BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Alcohol":                        BILL_LENS_CATEGORY_PALETTE.burgundy,

  // ── Retail ───────────────────────────────────────────────────────
  "Apparel & Accessories":          BILL_LENS_CATEGORY_PALETTE.burgundy,
  "Electronics":                    BILL_LENS_CATEGORY_PALETTE.ink2,
  "Home & Garden":                  BILL_LENS_CATEGORY_PALETTE.olive,
  "Toys & Games":                   BILL_LENS_CATEGORY_PALETTE.ochre,
  "Beauty & Personal Care":         BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Sports & Outdoors":              BILL_LENS_CATEGORY_PALETTE.forest,
  "Books & Stationery":             BILL_LENS_CATEGORY_PALETTE.kraft,
  "Fixtures & Displays":            BILL_LENS_CATEGORY_PALETTE.terracotta,

  // ── Hospitality ──────────────────────────────────────────────────
  "Bedding & Linen":                BILL_LENS_CATEGORY_PALETTE.muted,
  "Toiletries & Amenities":         BILL_LENS_CATEGORY_PALETTE.kraft,
  "Food & Beverage Supplies":       BILL_LENS_CATEGORY_PALETTE.olive,
  "Guest Services":                 BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Uniforms & Apparel":             BILL_LENS_CATEGORY_PALETTE.burgundy,
  "Marketing & Printing":           BILL_LENS_CATEGORY_PALETTE.kraft,
  "Utilities":                      BILL_LENS_CATEGORY_PALETTE.forest,

  // ── Healthcare ───────────────────────────────────────────────────
  "Medical Supplies":               BILL_LENS_CATEGORY_PALETTE.burgundy,
  "Pharmaceuticals":                BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Diagnostic Equipment":           BILL_LENS_CATEGORY_PALETTE.ink2,
  "Personal Protective Equipment":  BILL_LENS_CATEGORY_PALETTE.ochre,
  "Cleaning & Sterilisation":       BILL_LENS_CATEGORY_PALETTE.forest,
  "Office & Admin Supplies":        BILL_LENS_CATEGORY_PALETTE.muted,
  "Laboratory Supplies":            BILL_LENS_CATEGORY_PALETTE.forest,
  "Patient Care Items":             BILL_LENS_CATEGORY_PALETTE.burgundy,

  // ── Construction ─────────────────────────────────────────────────
  "Structural Materials":           BILL_LENS_CATEGORY_PALETTE.muted,
  "Electrical":                     BILL_LENS_CATEGORY_PALETTE.ochre,
  "Plumbing":                       BILL_LENS_CATEGORY_PALETTE.forest,
  "Concrete & Masonry":             BILL_LENS_CATEGORY_PALETTE.faint,
  "Timber & Framing":               BILL_LENS_CATEGORY_PALETTE.kraft,
  "Roofing & Cladding":             BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Insulation":                     BILL_LENS_CATEGORY_PALETTE.ochre,
  "Hire & Plant":                   BILL_LENS_CATEGORY_PALETTE.olive,
  "Subcontractors":                 BILL_LENS_CATEGORY_PALETTE.burgundy,

  // ── Manufacturing ────────────────────────────────────────────────
  "Raw Materials":                  BILL_LENS_CATEGORY_PALETTE.ink2,
  "Components & Parts":             BILL_LENS_CATEGORY_PALETTE.muted,
  "Machinery & Equipment":          BILL_LENS_CATEGORY_PALETTE.ink2,
  "Tooling":                        BILL_LENS_CATEGORY_PALETTE.kraft,
  "Energy & Utilities":             BILL_LENS_CATEGORY_PALETTE.ochre,
  "Chemicals & Consumables":        BILL_LENS_CATEGORY_PALETTE.forest,
  "Logistics & Freight":            BILL_LENS_CATEGORY_PALETTE.terracotta,

  // ── Technology ───────────────────────────────────────────────────
  "Hardware":                       BILL_LENS_CATEGORY_PALETTE.ink2,
  "Software & Licenses":            BILL_LENS_CATEGORY_PALETTE.muted,
  "Cloud & Hosting":                BILL_LENS_CATEGORY_PALETTE.forest,
  "Network & Communications":       BILL_LENS_CATEGORY_PALETTE.olive,
  "Security":                       BILL_LENS_CATEGORY_PALETTE.burgundy,
  "Consulting & Professional Services": BILL_LENS_CATEGORY_PALETTE.forest,
  "Maintenance & Support":          BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Training":                       BILL_LENS_CATEGORY_PALETTE.ochre,

  // ── Professional Services ────────────────────────────────────────
  "Office Supplies":                BILL_LENS_CATEGORY_PALETTE.muted,
  "Software & Subscriptions":       BILL_LENS_CATEGORY_PALETTE.ink2,
  "Professional Development":       BILL_LENS_CATEGORY_PALETTE.kraft,
  "Legal & Compliance":             BILL_LENS_CATEGORY_PALETTE.ink2,
  "Accounting & Finance":           BILL_LENS_CATEGORY_PALETTE.forest,
  "Travel & Accommodation":         BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Facilities":                     BILL_LENS_CATEGORY_PALETTE.olive,

  // ── Education ────────────────────────────────────────────────────
  "Textbooks & Learning Materials": BILL_LENS_CATEGORY_PALETTE.kraft,
  "Stationery & Art Supplies":      BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Sports & Recreation":            BILL_LENS_CATEGORY_PALETTE.olive,
  "Catering & Canteen":             BILL_LENS_CATEGORY_PALETTE.ochre,

  // ── Shared across sectors ────────────────────────────────────────
  "Packaging":                      BILL_LENS_CATEGORY_PALETTE.muted,
  "Packaging & Supplies":           BILL_LENS_CATEGORY_PALETTE.muted,
  "Cleaning & Chemicals":           BILL_LENS_CATEGORY_PALETTE.forest,
  "Cleaning & Maintenance":         BILL_LENS_CATEGORY_PALETTE.forest,
  "Equipment":                      BILL_LENS_CATEGORY_PALETTE.ink2,
  "Tools & Equipment":              BILL_LENS_CATEGORY_PALETTE.ink2,
  "Technology & Equipment":         BILL_LENS_CATEGORY_PALETTE.ink2,
  "Technology & Software":          BILL_LENS_CATEGORY_PALETTE.ink2,
  "Technology & Systems":           BILL_LENS_CATEGORY_PALETTE.ink2,
  "Furniture & Fixtures":           BILL_LENS_CATEGORY_PALETTE.kraft,
  "Safety Equipment":               BILL_LENS_CATEGORY_PALETTE.burgundy,
  "Maintenance & Repair":           BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Marketing & Advertising":        BILL_LENS_CATEGORY_PALETTE.terracotta,
  "Services":                       BILL_LENS_CATEGORY_PALETTE.forest,
  "Supplies":                       BILL_LENS_CATEGORY_PALETTE.muted,
  "Technology":                     BILL_LENS_CATEGORY_PALETTE.ink2,
  "Marketing":                      BILL_LENS_CATEGORY_PALETTE.terracotta,

  // ── Legacy / fallback ────────────────────────────────────────────
  "Meat":                           BILL_LENS_CATEGORY_PALETTE.burgundy,
  "Other":                          BILL_LENS_CATEGORY_PALETTE.faint,
};
